import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Plus, 
  Filter,
  TrendingUp,
  Users,
  FileWarning,
  RefreshCw,
  Trash2,
  Edit,
  BarChart3,
  Target
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Issue type icons
const ISSUE_ICONS = {
  sla_breach: <AlertTriangle className="h-4 w-4 text-red-500" />,
  contractor_delay: <Clock className="h-4 w-4 text-orange-500" />,
  missing_docs: <FileWarning className="h-4 w-4 text-yellow-500" />,
  system_confusion: <AlertCircle className="h-4 w-4 text-blue-500" />,
  comms_failure: <Users className="h-4 w-4 text-purple-500" />,
  duplicate_action: <RefreshCw className="h-4 w-4 text-pink-500" />,
  incorrect_status: <Target className="h-4 w-4 text-indigo-500" />,
  other: <BarChart3 className="h-4 w-4 text-gray-500" />,
};

// Outcome badges
const OUTCOME_COLORS = {
  open: "bg-red-100 text-red-700 border-red-200",
  workaround_used: "bg-yellow-100 text-yellow-700 border-yellow-200",
  resolved: "bg-green-100 text-green-700 border-green-200",
};

const OUTCOME_LABELS = {
  open: "Open",
  workaround_used: "Workaround",
  resolved: "Resolved",
};

const ACTOR_BADGES = {
  staff: "bg-teal-100 text-teal-700",
  contractor: "bg-blue-100 text-blue-700",
  client: "bg-purple-100 text-purple-700",
};

export default function PressureTestPage() {
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState({});
  const [enums, setEnums] = useState({ issue_types: [], outcomes: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  
  // Form state
  const [formData, setFormData] = useState({
    job_reference: "",
    park_name: "",
    contractor_name: "",
    issue_type: "",
    description: "",
    root_cause: "",
    outcome: "open",
    time_lost_minutes: 0,
    actor: "staff",
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [issuesRes, summaryRes, enumsRes] = await Promise.all([
        axios.get(`${API}/api/pressure-test/list?limit=200`, { headers }),
        axios.get(`${API}/api/pressure-test/summary`, { headers }),
        axios.get(`${API}/api/pressure-test/enums`, { headers }),
      ]);

      setIssues(issuesRes.data.issues || []);
      setSummary(summaryRes.data || {});
      setEnums(enumsRes.data || { issue_types: [], outcomes: [], actors: [] });
    } catch (err) {
      console.error("Failed to load data:", err);
      toast.error("Failed to load pressure test data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateIssue = async () => {
    try {
      if (!formData.issue_type) {
        toast.error("Please select an issue type");
        return;
      }
      if (!formData.description) {
        toast.error("Please enter a description");
        return;
      }

      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/pressure-test/log`, formData, { headers });
      
      toast.success("Issue logged successfully");
      setShowCreateDialog(false);
      setFormData({
        job_reference: "",
        park_name: "",
        contractor_name: "",
        issue_type: "",
        description: "",
        root_cause: "",
        outcome: "open",
        time_lost_minutes: 0,
        actor: "staff",
      });
      loadData();
    } catch (err) {
      console.error("Failed to create issue:", err);
      toast.error("Failed to log issue");
    }
  };

  const handleUpdateIssue = async () => {
    if (!editingIssue) return;
    
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/pressure-test/update/${editingIssue.id}`, {
        outcome: editingIssue.outcome,
        root_cause: editingIssue.root_cause,
        resolution_notes: editingIssue.resolution_notes,
        time_lost_minutes: editingIssue.time_lost_minutes,
      }, { headers });
      
      toast.success("Issue updated");
      setShowEditDialog(false);
      setEditingIssue(null);
      loadData();
    } catch (err) {
      console.error("Failed to update issue:", err);
      toast.error("Failed to update issue");
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm("Are you sure you want to delete this issue?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/api/pressure-test/issue/${issueId}`, { headers });
      
      toast.success("Issue deleted");
      loadData();
    } catch (err) {
      console.error("Failed to delete issue:", err);
      toast.error("Failed to delete issue");
    }
  };

  // Filter issues
  const filteredIssues = issues.filter(issue => {
    if (filterStatus !== "all" && issue.outcome !== filterStatus) return false;
    if (filterType !== "all" && issue.issue_type !== filterType) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="pressure-test-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="page-title">
            Pressure Test Tracker
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Capture real operational friction during go-live phase
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="log-issue-btn">
              <Plus className="h-4 w-4 mr-2" /> Log Issue
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Log Pressure Test Issue</DialogTitle>
              <DialogDescription>
                Record any friction or issues encountered during operation
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issue Type *</Label>
                  <Select 
                    value={formData.issue_type} 
                    onValueChange={(v) => setFormData({...formData, issue_type: v})}
                  >
                    <SelectTrigger data-testid="issue-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums.issue_types.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Who Encountered?</Label>
                  <Select 
                    value={formData.actor} 
                    onValueChange={(v) => setFormData({...formData, actor: v})}
                  >
                    <SelectTrigger data-testid="actor-select">
                      <SelectValue placeholder="Select actor" />
                    </SelectTrigger>
                    <SelectContent>
                      {enums.actors.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Reference</Label>
                  <Input 
                    placeholder="e.g., FM-20260422-0001"
                    value={formData.job_reference}
                    onChange={(e) => setFormData({...formData, job_reference: e.target.value})}
                    data-testid="job-reference-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Time Lost (minutes)</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={formData.time_lost_minutes}
                    onChange={(e) => setFormData({...formData, time_lost_minutes: parseInt(e.target.value) || 0})}
                    data-testid="time-lost-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Park Name</Label>
                  <Input 
                    placeholder="e.g., Brookfield Park"
                    value={formData.park_name}
                    onChange={(e) => setFormData({...formData, park_name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Contractor Name</Label>
                  <Input 
                    placeholder="e.g., CD Fire and Security"
                    value={formData.contractor_name}
                    onChange={(e) => setFormData({...formData, contractor_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea 
                  placeholder="What happened? Be specific..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  data-testid="description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Root Cause (if known)</Label>
                <Textarea 
                  placeholder="Why did this happen?"
                  value={formData.root_cause}
                  onChange={(e) => setFormData({...formData, root_cause: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Initial Outcome</Label>
                <Select 
                  value={formData.outcome} 
                  onValueChange={(v) => setFormData({...formData, outcome: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {enums.outcomes.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-teal-600 hover:bg-teal-700" 
                onClick={handleCreateIssue}
                data-testid="submit-issue-btn"
              >
                Log Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-teal-500" data-testid="total-issues-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Total Issues</div>
            <div className="text-2xl font-bold text-teal-700">{summary.total_issues || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-red-500" data-testid="open-issues-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Open</div>
            <div className="text-2xl font-bold text-red-600">{summary.open_issues || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500" data-testid="workaround-issues-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Workaround</div>
            <div className="text-2xl font-bold text-yellow-600">{summary.workaround_issues || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500" data-testid="resolved-issues-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Resolved</div>
            <div className="text-2xl font-bold text-green-600">{summary.resolved_issues || 0}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-orange-500" data-testid="time-lost-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Time Lost</div>
            <div className="text-2xl font-bold text-orange-600">
              {summary.total_time_lost_hours || 0}h
            </div>
            <div className="text-xs text-slate-400">
              ~{summary.avg_time_per_issue || 0} min/issue
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500" data-testid="top-issue-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Top Issue</div>
            <div className="text-lg font-bold text-purple-600 truncate">
              {summary.top_issue_type_label || "-"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribution Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Issues by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(summary.issues_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {ISSUE_ICONS[summary.issues_by_type_raw?.[type] ? type : 'other']}
                    <span className="text-sm">{type}</span>
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
              {Object.keys(summary.issues_by_type || {}).length === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">No issues logged yet</div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Issues by Actor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(summary.issues_by_actor || {}).map(([actor, count]) => (
                <div key={actor} className="flex items-center justify-between">
                  <span className="text-sm">{actor}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
              {Object.keys(summary.issues_by_actor || {}).length === 0 && (
                <div className="text-sm text-slate-400 text-center py-4">No issues logged yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Issues List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">All Issues</CardTitle>
            
            <div className="flex items-center gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32" data-testid="filter-status">
                  <Filter className="h-4 w-4 mr-1" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="workaround_used">Workaround</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {enums.issue_types.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredIssues.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No issues found</p>
                <p className="text-sm mt-1">Click "Log Issue" to record your first issue</p>
              </div>
            )}
            
            {filteredIssues.map((issue) => (
              <div 
                key={issue.id} 
                className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
                data-testid={`issue-card-${issue.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {ISSUE_ICONS[issue.issue_type]}
                      <span className="font-medium">{issue.job_reference || "No Job Ref"}</span>
                      <Badge className={`text-xs ${OUTCOME_COLORS[issue.outcome]}`}>
                        {OUTCOME_LABELS[issue.outcome]}
                      </Badge>
                      <Badge className={`text-xs ${ACTOR_BADGES[issue.actor]}`}>
                        {issue.actor}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-2">{issue.description}</p>
                    
                    {issue.root_cause && (
                      <p className="text-xs text-slate-500 mb-1">
                        <strong>Root cause:</strong> {issue.root_cause}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                      {issue.park_name && <span>{issue.park_name}</span>}
                      {issue.contractor_name && <span>{issue.contractor_name}</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {issue.time_lost_minutes} min
                      </span>
                      <span>
                        {new Date(issue.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setEditingIssue(issue);
                        setShowEditDialog(true);
                      }}
                      data-testid={`edit-issue-${issue.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteIssue(issue.id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`delete-issue-${issue.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Issue</DialogTitle>
            <DialogDescription>
              Update the status, root cause, or resolution notes
            </DialogDescription>
          </DialogHeader>
          
          {editingIssue && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Outcome</Label>
                <Select 
                  value={editingIssue.outcome} 
                  onValueChange={(v) => setEditingIssue({...editingIssue, outcome: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {enums.outcomes.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Root Cause</Label>
                <Textarea 
                  value={editingIssue.root_cause || ""}
                  onChange={(e) => setEditingIssue({...editingIssue, root_cause: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Resolution Notes</Label>
                <Textarea 
                  placeholder="How was this resolved?"
                  value={editingIssue.resolution_notes || ""}
                  onChange={(e) => setEditingIssue({...editingIssue, resolution_notes: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Time Lost (minutes)</Label>
                <Input 
                  type="number"
                  min="0"
                  value={editingIssue.time_lost_minutes || 0}
                  onChange={(e) => setEditingIssue({...editingIssue, time_lost_minutes: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700" 
              onClick={handleUpdateIssue}
              data-testid="update-issue-btn"
            >
              Update Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Instructions */}
      <Card className="bg-teal-50 border-teal-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <TrendingUp className="h-5 w-5 text-teal-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-teal-800 mb-1">How to Use This Tracker</h3>
              <p className="text-sm text-teal-700">
                EVERY time something slows you down, confuses a contractor, or breaks flow — <strong>LOG IT</strong>.
                After 1-2 weeks, patterns will emerge. Fix the highest impact items first.
                That is how you reach a true 10/10 system.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
